import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  getMyIpDetail, getMyIpProjects, normalizeIpId, updateMyIp,
} from '$/domains/myIp/myIpService';
import type { MyIpItem, MyIpProject } from '$/domains/myIp/types';
import { getMyIpEditDraft, type MyIpEditDraft } from '$/domains/myIp/editProfile';
import { reportMyIpManualEdit } from '$/domains/myIp/manualEditEvent';

export interface MyIpDetailState {
  contextKey: string;
  status: 'idle' | 'loading' | 'denied' | 'invalid' | 'success' | 'error';
  item?: MyIpItem;
  error?: unknown;
  projects: MyIpProject[];
  projectTotal?: number;
  projectsStatus: 'idle' | 'loading' | 'success' | 'error';
  projectsError?: unknown;
  hasMoreProjects: boolean;
}

/** 关联数量未读到时保持 undefined，不能把失败表现为零个项目。 */
function emptyState(contextKey: string, status: MyIpDetailState['status']): MyIpDetailState {
  return {
    contextKey, status, projects: [], projectsStatus: 'idle', hasMoreProjects: false,
  };
}

/** 独立 URL 加载详情，关联项目的错误和重试不影响已读取的角色设定。 */
export function useMyIpDetail(ipId: string, scopeKey: string, enabled: boolean, viewerUserId: string) {
  const contextKey = JSON.stringify([scopeKey, ipId]);
  const current = useRef({ contextKey, enabled });
  current.current = { contextKey, enabled };
  const [state, setState] = useState<MyIpDetailState>(() => emptyState(contextKey, 'idle'));
  const liveItem = useRef<MyIpItem>();
  liveItem.current = enabled && state.contextKey === contextKey && state.status === 'success' ? state.item : undefined;
  const [revision, setRevision] = useState(0);
  const epoch = useRef(0);
  const controller = useRef<AbortController>();
  const projectPage = useRef(0);
  const received = useRef(0);
  const omittedProjects = useRef(0);
  const projectPending = useRef(false);
  const projectsReadyEpoch = useRef<number>();
  const projectsExhausted = useRef(false);
  const savePending = useRef(false);
  const saveNeedsRefresh = useRef(false);

  /** 保留已加载项目；失败仍请求原页，原始条数用于判断分页进度。 */
  const loadProjects = useCallback(async () => {
    const abort = controller.current;
    if (!enabled || projectPending.current || !abort || abort.signal.aborted
      || projectsReadyEpoch.current !== epoch.current || projectsExhausted.current
      || current.current.contextKey !== contextKey || !current.current.enabled) return;
    const requestEpoch = epoch.current;
    const nextPage = projectPage.current + 1;
    projectPending.current = true;
    /** URL/账号/空间与请求版本都匹配时才提交结果。 */
    const isCurrent = () => !abort.signal.aborted && epoch.current === requestEpoch
      && current.current.enabled && current.current.contextKey === contextKey;
    setState((previous) => ({ ...previous, projectsStatus: 'loading', projectsError: undefined }));
    try {
      const result = await getMyIpProjects(ipId, { page: nextPage, pageSize: 6, signal: abort.signal });
      if (!isCurrent()) return;
      // 测试宿主和灰度旧实现可能尚未返回分页元信息，回退到当前页列表长度。
      const sourceCount = result.sourceCount ?? result.list.length;
      const omittedCount = result.omittedCount ?? 0;
      projectPage.current = nextPage;
      received.current += sourceCount;
      omittedProjects.current += omittedCount;
      projectsExhausted.current = sourceCount === 0 || received.current >= result.total;
      setState((previous) => {
        const ids = new Set(previous.projects.map((project) => project.projectId));
        const projects = [...previous.projects];
        result.list.forEach((project) => {
          if (!ids.has(project.projectId)) {
            ids.add(project.projectId);
            projects.push(project);
          }
        });
        return {
          ...previous,
          projects,
          projectTotal: Math.max(0, result.total - omittedProjects.current),
          projectsStatus: 'success',
          hasMoreProjects: sourceCount > 0 && received.current < result.total,
        };
      });
    } catch (projectsError) {
      if (isCurrent()) setState((previous) => ({ ...previous, projectsStatus: 'error', projectsError }));
    } finally {
      if (isCurrent()) projectPending.current = false;
    }
  }, [contextKey, ipId, enabled]);

  useEffect(() => {
    epoch.current += 1;
    const requestEpoch = epoch.current;
    const abort = new AbortController();
    controller.current = abort;
    projectPage.current = 0;
    received.current = 0;
    omittedProjects.current = 0;
    projectPending.current = false;
    projectsReadyEpoch.current = undefined;
    projectsExhausted.current = false;
    savePending.current = false;
    saveNeedsRefresh.current = false;
    setState(emptyState(contextKey, enabled ? 'loading' : 'idle'));
    /** URL 切换当次 render 已隐藏旧详情，迟到网络响应也不能恢复它。 */
    const isCurrent = () => !abort.signal.aborted && epoch.current === requestEpoch
      && current.current.enabled && current.current.contextKey === contextKey;
    /** 详情独立鉴权；只有个人 IP 才启用自有资产关联项目接口。 */
    const load = async () => {
      try {
        normalizeIpId(ipId);
      } catch {
        if (isCurrent()) setState(emptyState(contextKey, 'invalid'));
        return;
      }
      try {
        const item = await getMyIpDetail(ipId, abort.signal);
        if (!isCurrent()) return;
        // 详情接口已完成访问鉴权；仅在明确返回其他创建者时拦截，兼容历史响应缺少 creator_id。
        if (item.assetType === 'personal' && item.creatorId && item.creatorId !== viewerUserId) {
          throw Object.assign(new Error('Private IP is only available to its creator'), { status: 403 });
        }
        setState({ ...emptyState(contextKey, 'success'), item });
        // 官方 IP 允许浏览，但 showcase/projects 仅支持自有 personal IP。
        if (item.assetType === 'personal') {
          projectsReadyEpoch.current = requestEpoch;
          loadProjects();
        }
      } catch (error) {
        if (isCurrent()) setState({ ...emptyState(contextKey, 'error'), error });
      }
    };
    if (enabled) load();
    return () => { abort.abort(); };
  }, [contextKey, enabled, ipId, revision, loadProjects, viewerUserId]);

  /** 关联项目正常态自动耗尽服务端分页，详情页始终平铺展示而不暴露更多按钮。 */
  useEffect(() => {
    if (state.status !== 'success' || state.projectsStatus !== 'success' || !state.hasMoreProjects) return;
    loadProjects();
  }, [loadProjects, state.hasMoreProjects, state.projectsStatus, state.status]);

  /** 详情重试及外部更新后重新验证资格，旧项目分页同时失效。 */
  const refresh = useCallback(() => {
    epoch.current += 1;
    controller.current?.abort();
    setState(emptyState(contextKey, enabled ? 'loading' : 'idle'));
    setRevision((value) => value + 1);
  }, [contextKey, enabled]);

  /** 手动保存成功且仍在当前空间时同步 Agent 上下文；事件失败不回滚已保存主档。 */
  const save = useCallback(async (item: MyIpItem, draft: MyIpEditDraft, locale: string) => {
    const abort = controller.current;
    if (!enabled || savePending.current || !abort || abort.signal.aborted
      || state.status !== 'success' || state.item !== item || liveItem.current !== item || state.contextKey !== contextKey
      || !current.current.enabled || current.current.contextKey !== contextKey) throw new Error('IP edit context expired');
    const requestEpoch = epoch.current;
    savePending.current = true;
    try {
      const manualChanged = JSON.stringify(draft) !== JSON.stringify(getMyIpEditDraft(item));
      // 失败不锁死编辑；下一次普通保存先确认最新版本，保留当前输入。
      const base = saveNeedsRefresh.current ? await getMyIpDetail(item.ipId, abort.signal) : item;
      if (abort.signal.aborted || epoch.current !== requestEpoch
        || !current.current.enabled || current.current.contextKey !== contextKey) throw new Error('IP edit context expired');
      const updated = await updateMyIp(base, draft, locale, abort.signal, item);
      if (abort.signal.aborted || epoch.current !== requestEpoch
        || !current.current.enabled || current.current.contextKey !== contextKey) throw new Error('IP edit context expired');
      saveNeedsRefresh.current = false;
      setState((previous) => ({ ...previous, item: updated }));
      if (manualChanged) {
        await reportMyIpManualEdit(base, updated, abort.signal);
      }
    } catch (error) {
      if (epoch.current === requestEpoch) saveNeedsRefresh.current = true;
      throw error;
    } finally {
      if (epoch.current === requestEpoch) savePending.current = false;
    }
  }, [contextKey, enabled, state]);

  const visibleState = enabled && state.contextKey === contextKey
    ? state : emptyState(contextKey, enabled ? 'loading' : 'idle');
  return {
    ...visibleState, refresh, loadProjects, save,
  };
}
